import React, { useEffect, useMemo, useState } from 'react';
import Radio from 'components/commercetools-ui/atoms/radio';
import { useAccount, useCart } from 'frontastic';
import useMappers from '../../hooks/useMappers';
import { Address } from '../../types';

interface Props {
  className?: string;
  type: 'shipping' | 'billing';
  onSelectAddress: (address: Address) => void;
  retainData?: boolean;
  resetAddressData?: string;
}

const AccountAddresses: React.FC<Props> = ({
  className = '',
  type,
  onSelectAddress,
  retainData = false,
  resetAddressData,
}) => {
  const { shippingAddresses, billingAddresses, defaultBillingAddress, defaultShippingAddress } = useAccount();
  const { data } = useCart();

  const { accountAddressToAddress } = useMappers();

  const addresses = useMemo(() => {
    let defaultAddress;
    let otherAddresses: any[] = [];

    if (shippingAddresses) {
      defaultAddress = shippingAddresses.find((address) => address.isDefaultShippingAddress);
      otherAddresses = shippingAddresses.filter((address) => !address.isDefaultShippingAddress);
    }

    if (type !== 'shipping') {
      if (billingAddresses) {
        defaultAddress = billingAddresses.find((address) => address.isDefaultBillingAddress);
        otherAddresses = billingAddresses.filter((address) => !address.isDefaultBillingAddress);
      }
    }

    return defaultAddress ? [defaultAddress, ...otherAddresses] : [...otherAddresses];
  }, [type, shippingAddresses, billingAddresses]);

  const [selected, setSelected] = useState<Address>();

  useEffect(() => {
    const defaultAddress = type === 'shipping' ? defaultShippingAddress : defaultBillingAddress;
    const checkoutAddresses = type === 'shipping' ? shippingAddresses : billingAddresses;
    if (retainData) {
      if (type === 'shipping' && data?.shippingAddress?.postalCode) {
        setSelected(accountAddressToAddress(data?.shippingAddress));
        return;
      } else if (data?.billingAddress?.postalCode) {
        setSelected(accountAddressToAddress(data?.billingAddress));
        return;
      }
    }
    if (defaultAddress) {
      setSelected(accountAddressToAddress(defaultAddress));
      return;
    }

    if (checkoutAddresses.some((address) => address.isDefaultShippingAddress)) {
      const defaultAdd = checkoutAddresses.find((address) => address.isDefaultShippingAddress);
      if (defaultAdd) {
        setSelected(accountAddressToAddress(defaultAdd));
      }
    } else {
      checkoutAddresses.length > 0 && setSelected(accountAddressToAddress(checkoutAddresses[0]));
    }
  }, [defaultShippingAddress, defaultBillingAddress, accountAddressToAddress, type]);

  useEffect(() => {
    if (selected) onSelectAddress(selected);
  }, [selected]);

  return (
    <div className={`flex grid-cols-1 flex-col gap-20 md:grid md:grid-cols-3 ${className}`}>
      {addresses
        .map((address) => accountAddressToAddress(address))
        .map((address) => (
          <div
            key={address.addressId}
            className="flex cursor-pointer items-center gap-20 rounded-md border border-neutral-400 px-20 py-38"
            onClick={() => setSelected(address)}
          >
            <Radio className="shrink-0" checked={address.addressId === selected?.addressId} />
            <p className="max-w-full overflow-hidden text-14 text-secondary-black">
              {address.firstName} {address.lastName}
              <span className="mt-8 block" />
              {address.line1}
              <span className="mt-8 block" />
              {address.postalCode} {address.city}
            </p>
          </div>
        ))}
    </div>
  );
};

export default AccountAddresses;
